import { type FC, useEffect, useRef } from 'react';
import { Chart, type LegendItem, type TooltipItem } from 'chart.js/auto';
import { optionalProp } from '../core';
import type { BaseChartProps, BaseChartScale, BaseChartToolTip, ChartProps, TextStyle } from './types/propType';
import { BaseChartType } from './types/enums';
import styles from './BaseChart.module.scss';

Chart.register();
const defaultFontSize = 14;
const defaultFontFamily = `'GT-Walsheim-Pro-Regular', 'Noto-Sans-Thai-Regular', 'GT-Walsheim-Pro-Regular-Fallback-Arial', sans-serif`;
const defaultFontColor = '#2c2c2c';

export const renderLegends = (
  chart: Chart,
  chartId: string,
  chartType: BaseChartType,
  fontSize: number | undefined,
  fontFamily: string | undefined,
  displayLegend: boolean,
  orientation: 'vertical' | 'horizontal' = 'horizontal', // New orientation flag
) => {
  const legend = document.getElementById(chartId);
  if (legend && displayLegend === true) {
    legend.innerHTML = '';
    const items: LegendItem[] | undefined = chart?.options?.plugins?.legend?.labels?.generateLabels?.(chart);

    const ulElement = document.createElement('ul');
    ulElement.setAttribute('id', `chart-data-point`);
    ulElement.classList.add(`${styles['ul-element']}`);

    // Apply orientation-specific styles
    ulElement.classList.add(orientation === 'vertical' ? `${styles['vertical']}` : `${styles['horizontal']}`);

    items?.forEach((item, datasetIndex) => {
      const liElement = document.createElement('li');
      const labelElement = document.createElement('span');

      // Apply custom font styles
      labelElement.style.fontSize = `${getNumberValue(fontSize, 14)}px`;
      labelElement.style.fontFamily = getFontFamily(fontFamily, defaultFontFamily);

      const element = document.createElement('span');
      switch (chartType) {
        case BaseChartType.LINE: {
          if (item.lineDash && item.lineDash.length > 0) {
            element.style.borderBottom = `2px dashed ${item.strokeStyle as string}`;
            element.style.borderWidth = '3px';
          } else {
            element.style.borderBottom = `2px solid ${item.strokeStyle as string}`;
            element.style.borderWidth = '3px';
          }

          labelElement.appendChild(document.createTextNode(`${item.text}`));
          break;
        }
        case BaseChartType.DOUGHNUT: {
          element.style.border = `2px solid ${item.fillStyle as string}`;
          element.style.width = '10px';
          element.style.height = '10px';
          element.style.borderRadius = '50%';
          element.style.backgroundColor = `${item.fillStyle as string}`;
          const [dataset] = chart.data.datasets;
          labelElement.appendChild(document.createTextNode(`${item.text}: ${dataset?.data[datasetIndex]}`));
          break;
        }
        case BaseChartType.BAR:
        default: {
          element.style.border = `2px solid ${item.strokeStyle as string}`;
          element.style.width = '10px';
          element.style.height = '10px';
          element.style.borderRadius = '50%';
          element.style.backgroundColor = `${item.strokeStyle as string}`;

          labelElement.appendChild(document.createTextNode(`${item.text}`));
          break;
        }
      }

      element.classList.add(`${styles['element']}`);
      liElement.classList.add(`${styles['li-element']}`);
      labelElement.classList.add(`${styles['label-element']}`);

      liElement.appendChild(element);
      liElement.appendChild(labelElement);
      ulElement.appendChild(liElement);
    });

    legend.appendChild(ulElement);
  }
};

export const renderDataLabels = (
  chart: Chart,
  displayDataLabel: boolean,
  isHorizontal: boolean,
  scale: BaseChartScale | undefined,
) => {
  if (displayDataLabel) {
    const { ctx } = chart;

    // Get the maximum value in the dataset to check against for position adjustments
    const maxValueX = chart.scales.x?.max as number;
    const maxValueY = chart.scales.y?.max as number;

    // Loop through all datasets
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);

      meta.data.forEach((bar, index) => {
        const value = dataset.data[index] as number;

        // Calculate and handling the x and y data label position
        const x = getLabelPosition(
          isHorizontal,
          value,
          maxValueX,
          chart.scales.x?.getPixelForValue(maxValueX) as number,
          bar.x,
          'x',
        );
        const y = getLabelPosition(
          isHorizontal,
          value,
          maxValueY,
          chart.scales.y?.getPixelForValue(maxValueY) as number,
          bar.y,
          'y',
        );

        ctx.save();
        ctx.fillStyle = styles.darkGrayOnWhite as string; // Set text color
        ctx.font = `normal ${getNumberValue(scale?.fontSize, defaultFontSize)}px ${getFontFamily(
          scale?.fontFamily,
          defaultFontFamily,
        )}`; // Set font style
        ctx.textAlign = 'center'; // Center the text
        ctx.fillText(scale?.formattedValue?.replace('{0}', value.toString()) ?? value.toString(), x, y); // Draw the text label
        ctx.restore();
      });
    });
  }
};

const getLabelPosition = (
  isHorizontal: boolean,
  value: number,
  maxValue: number,
  pixelValue: number,
  bar: number,
  scale: 'x' | 'y',
) => {
  const padding = 30; // Padding inside the bar for label positioning

  if (scale === 'x') {
    return isHorizontal ? (value >= maxValue * 0.95 ? pixelValue - padding : bar + 20) : bar;
  } else {
    return isHorizontal ? bar + 5 : value >= maxValue * 0.95 ? pixelValue + padding : bar - 10;
  }
};

const getTooltip = (props: BaseChartToolTip, formattedValue?: string) => ({
  backgroundColor: props.backgroundColor ?? styles.darkPurple, // Custom background color
  displayColors: props.displayColorBox ?? false, // Hide the color box
  callbacks: {
    title: function () {
      return props.title ?? '';
    },
    label: function (context: TooltipItem<'bar' | 'line' | 'doughnut'>) {
      // Either use custom labelText or default label
      const label = props.labelText !== undefined ? props.labelText : context.label || '';
      const value = formattedValue ? formattedValue.replace('{0}', context.raw as string) : context.raw;
      return `${label} ${value}`;
    },
    labelTextColor: function () {
      return props.labelTextColor ?? styles.amwayWhite;
    },
  },
});

const getBooleanValue = (params: boolean | undefined) => (params === undefined ? false : params);

const getNumberValue = (params: number | undefined, defaultValue: number) =>
  params === undefined ? defaultValue : params;

const getArrayValue = (params: Array<number> | undefined) => (params === undefined ? [] : params);

const getFontFamily = (params: string | undefined, defaultValue: string) =>
  params === undefined ? defaultValue : params;

const getTitle = (params: string | undefined, fontSize: number | undefined, fontFamily: string | undefined) =>
  params === undefined
    ? {}
    : {
        display: true,
        text: params,
        font: {
          size: fontSize ?? defaultFontSize,
          family: fontFamily ?? defaultFontFamily,
        },
      };

const getTicks = (
  formattedValue: string | undefined,
  stepSize: number | undefined,
  indexAxis: 'x' | 'y' | undefined,
  scaleType: 'x' | 'y',
  fontSize: number | undefined,
  fontFamily: string | undefined,
) => {
  const step = stepSize ?? 10;

  // Check scaleType is same with indexAxis only apply logic
  return formattedValue && indexAxis && indexAxis === scaleType
    ? {
        stepSize: step,
        font: {
          size: fontSize ?? defaultFontSize,
          family: fontFamily ?? defaultFontFamily,
        },
        // callback: (value: number) => formattedValue.replace('{0}', value.toString()),
        callback: (tickValue: string | number) => {
          // Ensure tickValue is treated as a number for replacement
          const value = typeof tickValue === 'number' ? tickValue : parseFloat(tickValue);

          if (formattedValue && indexAxis) {
            return formattedValue.replace('{0}', value.toString());
          }
          return value.toString();
        },
      }
    : {
        stepSize: step,
        font: {
          size: fontSize ?? defaultFontSize,
          family: fontFamily ?? defaultFontFamily,
        },
      };
};

export const renderDoughnutTitle = (
  chart: Chart,
  doughnutTotalValue: TextStyle | undefined,
  doughnutDescription: TextStyle | undefined,
) => {
  if (doughnutTotalValue) {
    const { width, height, ctx } = chart;
    ctx.restore();

    ctx.font = `${getNumberValue(doughnutTotalValue.fontSize, 14)}px ${getFontFamily(
      doughnutTotalValue.fontFamily,
      defaultFontFamily,
    )}`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';

    const centerX = width / 2;
    const centerY = height / 2;
    ctx.fillStyle = getFontFamily(doughnutTotalValue.fontColor, defaultFontColor);

    // Use the centerText prop
    ctx.fillText(doughnutTotalValue.text ?? '', centerX, centerY);

    // Render subtitle (if present)
    if (doughnutDescription) {
      // Calculate Y position for subtitle below the title
      const subtitleFontSize = getNumberValue(doughnutDescription.fontSize, 12);
      const titleFontSize = getNumberValue(doughnutTotalValue.fontSize, 14);

      ctx.font = `${subtitleFontSize}px ${getFontFamily(doughnutDescription.fontFamily, defaultFontFamily)}`;
      ctx.fillStyle = getFontFamily(doughnutDescription.fontColor, defaultFontColor);

      // Position subtitle slightly below title by offsetting the Y position
      const subtitleY = centerY + titleFontSize; // Adjust as needed for spacing
      ctx.fillText(doughnutDescription.text ?? '', centerX, subtitleY);
    }
    ctx.save();
  }
};

const BaseChart: FC<BaseChartProps> = (props: BaseChartProps) => {
  const {
    chartId,
    data,
    tooltip,
    xScale,
    yScale,
    indexAxis,
    legendContainerClassName = '',
    baseChartType,
    legendFontSize,
    legendFontFamily,
    displayDataLabel = false,
    displayLegend = true,
    doughnutTotalValue,
    doughnutDescription,
    cutout = '80%', // Doughnut Cutout Size
    legendOrientation = 'horizontal',
  } = props;

  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<Chart<'bar' | 'line' | 'doughnut', number[], string> | null>(null);
  const isHorizontal = indexAxis === 'y';
  const scale = isHorizontal ? xScale : yScale;

  useEffect(() => {
    if (chartRef.current) {
      const ctx = chartRef.current.getContext('2d');
      if (ctx) {
        chartInstance.current = new Chart(ctx, {
          type: baseChartType,
          data: data,
          options: {
            indexAxis: indexAxis,
            responsive: true,
            cutout: cutout,
            aspectRatio: 2, // Doughnut Size
            plugins: {
              legend: {
                display: false,
              },
              ...{ ...optionalProp('tooltip', tooltip && getTooltip(tooltip, scale?.formattedValue)) },
            },
            scales:
              baseChartType !== BaseChartType.DOUGHNUT
                ? {
                    x: {
                      beginAtZero: getBooleanValue(xScale?.beginAtZero),
                      grid: {
                        display: getBooleanValue(xScale?.displayGrid),
                      },
                      border: {
                        display: getBooleanValue(xScale?.displayBorder),
                        dash: getArrayValue(xScale?.borderDash),
                      },
                      max: getNumberValue(xScale?.maxNumber, 60),
                      ticks: getTicks(
                        xScale?.formattedValue,
                        xScale?.stepSize,
                        indexAxis,
                        'x',
                        xScale?.fontSize,
                        xScale?.fontFamily,
                      ),
                      title: getTitle(xScale?.title, xScale?.fontSize, xScale?.fontFamily),
                    },
                    y: {
                      beginAtZero: getBooleanValue(yScale?.beginAtZero),
                      max: getNumberValue(yScale?.maxNumber, 60),
                      ticks: getTicks(
                        yScale?.formattedValue,
                        yScale?.stepSize,
                        indexAxis,
                        'y',
                        yScale?.fontSize,
                        yScale?.fontFamily,
                      ),
                      border: {
                        display: getBooleanValue(yScale?.displayBorder),
                        dash: getArrayValue(yScale?.borderDash),
                      },
                      grid: {
                        display: getBooleanValue(yScale?.displayGrid),
                      },
                      title: getTitle(yScale?.title, yScale?.fontSize, yScale?.fontFamily),
                    },
                  }
                : {},
          },
          plugins: [
            {
              id: 'custom-legend',
              afterUpdate: (chart) => {
                renderLegends(
                  chart as Chart,
                  chartId,
                  baseChartType,
                  legendFontSize,
                  legendFontFamily,
                  displayLegend,
                  legendOrientation,
                );
              },
            },
            {
              id: 'customLabels',
              afterDatasetDraw: (chart) => {
                renderDataLabels(chart as Chart, displayDataLabel, isHorizontal, scale);
              },
            },
            {
              id: 'customDoughnutCenterTitle',
              beforeDraw: (chart) => {
                renderDoughnutTitle(chart as Chart, doughnutTotalValue, doughnutDescription);
              },
            },
          ],
        });
      }
    }
  }, [chartId]);

  useEffect(() => {
    if (chartInstance.current) {
      // Update the chart data when the dataset changes
      chartInstance.current.data = data;
      chartInstance.current.update(); // Re-render the chart with new data
    }
  }, [data]); // Re-run effect when data changes

  return (
    <div data-testid="base-chart-test-id" className={`${styles['chart-container']}`}>
      <canvas data-testid="canvas-test-id" ref={chartRef}></canvas>
      <div id={chartId ?? 'nextgen-base-chart-legend'} className={`${legendContainerClassName ?? ''}`}></div>
    </div>
  );
};

export default BaseChart;
export type { ChartProps };
export { getTooltip, getBooleanValue, getNumberValue, getTicks, getTitle, getArrayValue, getFontFamily };
